import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTallyForms } from "../../api/queries/tally/useTallyForms";
import { useToggleTallyFormArchive } from "../../api/queries/tally/useToggleTallyFormArchive";
import { formatDateTime } from "../../common/utils";
import { TallyForm } from "../../api/types/tally";
import { appToastContext } from "../../context/toast";

export default function TallyFormList() {
  const navigate = useNavigate();
  const { data, isLoading } = useTallyForms();
  const forms: TallyForm[] = data?.data?.forms ?? [];
  const toggleArchiveMutation = useToggleTallyFormArchive();
  const { ref: appToastRef } = useContext(appToastContext);

  const refreshedTemplate = (rowData: TallyForm) =>
    rowData.refreshedAt ? formatDateTime(rowData.refreshedAt) : "—";

  const archivedTemplate = (rowData: TallyForm) =>
    rowData.archived ? "Yes" : "No";

  const nameTemplate = (rowData: TallyForm) => (
    <Link to={`/tally/forms/${rowData.id}`}>{rowData.name}</Link>
  );

  const showToast = (
    severity: "success" | "info" | "warn" | "error",
    summary: string,
  ) => {
    if (!appToastRef?.current) {
      return;
    }

    appToastRef.current.show({
      severity,
      summary,
    });
  };

  const formIdTemplate = (rowData: TallyForm) => (
    <a
      href={`https://tally.so/r/${rowData.formId}`}
      target="_blank"
      rel="noreferrer"
    >
      {rowData.formId}
    </a>
  );

  const handleToggleArchive = (form: TallyForm) => {
    const actionLabel = form.archived ? "Unarchive" : "Archive";
    const formLabel = form.name || form.formId;

    confirmDialog({
      message: `Are you sure you want to ${actionLabel.toLowerCase()} "${formLabel}"?`,
      header: `${actionLabel} Form`,
      icon: "pi pi-exclamation-triangle",
      acceptClassName: form.archived ? "p-button-info" : "p-button-warning",
      accept: async () => {
        try {
          await toggleArchiveMutation.mutateAsync(form.id);
          showToast(
            "success",
            `Form "${formLabel}" ${actionLabel.toLowerCase()}d.`,
          );
        } catch (error: any) {
          const errorMessage =
            error?.response?.data?.message ??
            `Unable to ${actionLabel.toLowerCase()} form.`;
          showToast("error", errorMessage);
        }
      },
    });
  };

  const actionsTemplate = (rowData: TallyForm) => (
    <Button
      label={rowData.archived ? "Unarchive" : "Archive"}
      icon={rowData.archived ? "pi pi-undo" : "pi pi-archive"}
      className="p-button-text p-button-sm"
      onClick={() => handleToggleArchive(rowData)}
    />
  );

  return (
    <div className="col-12">
      <div className="flex py-3 justify-content-between align-items-center">
        <div className="flex flex-column">
          <h1 className="m-0">Tally Forms</h1>
          <span className="text-500">
            Stored forms that have been used for question mappings.
          </span>
        </div>
        <Button
          label="Add Form"
          icon="pi pi-plus"
          onClick={() => navigate("/tally/forms/add")}
        />
      </div>

      <div className="card p-4">
        <DataTable
          value={forms}
          loading={isLoading}
          emptyMessage="No forms stored yet."
          stripedRows
          responsiveLayout="scroll"
        >
          <Column field="formId" header="Form ID" body={formIdTemplate} />
          <Column field="name" header="Name" body={nameTemplate} />
          <Column
            field="refreshedAt"
            header="Refreshed"
            body={refreshedTemplate}
          />
          <Column
            field="archived"
            header="Archived"
            body={archivedTemplate}
            style={{ width: "8rem" }}
          />
          <Column
            header="Actions"
            body={actionsTemplate}
            style={{ width: "10rem" }}
          />
        </DataTable>
      </div>

      <ConfirmDialog />
    </div>
  );
}
